import { createStore } from '@/shared/stores/config';
import type { ETourId } from './types';

/**
 * Tour management store state.
 *
 * Ensures only one tour runs at a time across the entire app.
 * ManagedTourProvider checks `activeTourId` before starting a tour
 * and clears it on completion, skip, or unmount.
 */
interface ITourStoreState {
  /**
   * The currently active tour ID, or null if no tour is running.
   * Used as a mutex — a tour's `startTour` bails out when another tour owns this slot.
   */
  activeTourId: ETourId | null;

  /**
   * Claims the active slot for the given tour.
   *
   * @param tourId - Tour that is about to start
   */
  setActiveTour: (tourId: ETourId) => void;

  /**
   * Releases the active slot so another tour can start.
   * Called on tour completion, skip, or when ManagedTourProvider unmounts.
   */
  clearActiveTour: () => void;
}

/**
 * Zustand store that serializes tour execution.
 *
 * @example
 * ```ts
 * const activeTourId = useTourStore(selectActiveTourId);
 * const setActiveTour = useTourStore(selectSetActiveTour);
 * ```
 */
export const useTourStore = createStore<ITourStoreState>(
  'tour-store',
  (_, _get, api) => ({
    activeTourId: null,

    setActiveTour: (tourId: ETourId) => {
      api.setState({ activeTourId: tourId }, false, {
        type: 'tour/setActiveTour',
        tourId,
      });
    },

    clearActiveTour: () => {
      api.setState({ activeTourId: null }, false, 'tour/clearActiveTour');
    },
  }),
);

/** Selects the currently active tour ID (null when idle). */
export const selectActiveTourId = (state: ITourStoreState) =>
  state.activeTourId;

/** Selects the action to claim the active tour slot. */
export const selectSetActiveTour = (state: ITourStoreState) =>
  state.setActiveTour;

/** Selects the action to release the active tour slot. */
export const selectClearActiveTour = (state: ITourStoreState) =>
  state.clearActiveTour;
